import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import CgIcon from "../common/CgIcon";
import { formatRupees } from "../common/money";
import { PAYMENT_MODES } from "../../PatientsIP/ReceptionistIp/BillingIp/PaymentModeSelector";
import styles from "./saleEntry.module.css";

const round2 = (value) => Math.round(Number(value || 0) * 100) / 100;

const money = (raw) => {
  const value = parseFloat(String(raw).replace(/[^0-9.]/g, ""));
  return Number.isFinite(value) && value > 0 ? value : 0;
};


/*
 * Taking money, wherever money is taken.
 *
 * Sale Entry raises a bill with it and will accept nothing being paid - the
 * bill then stands unpaid. Billing collects against a bill that already exists,
 * where nothing paid is not an outcome, so it asks for an amount and says so if
 * one is missing.
 */
const RecordPaymentModal = ({
  open,
  onCancel,
  onConfirm,
  due,
  subtitle,
  working,
  title = "Record Payment",
  action = "Generate Bill",
  requireAmount = false,
}) => {
  const payable = round2(due);
  const [mode, setMode] = useState("cash");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    setMode("cash");
    setAmount(String(payable));
    setReference("");
    setError("");
  }, [open]);


  const needsReference = Boolean(mode) && mode !== "cash";

  const received = money(amount);
  const balance = round2(payable - received);
  const overpaying = received > payable + 0.009;

  const submitLabel = useMemo(
    () => (received ? `${action} · ${formatRupees(received)}` : action),
    [received, action]
  );

  const confirm = () => {
    if (overpaying) {
      setError("That is more than the bill total.");
      return;
    }
    if (requireAmount && !received) {
      setError("Enter how much is being collected.");
      return;
    }
    if (received && !mode) {
      setError("Choose how this was paid.");
      return;
    }
    onConfirm(
      received
        ? {
            payment_type: "advance",
            payment_mode: mode,
            amount: received,
            transaction_reference: needsReference
              ? reference.trim() || undefined
              : undefined,
          }
        : null
    );
  };

  return (
    <Modal
      className={styles.seDialog}
      open={open}
      centered
      width={520}
      maskClosable={!working}
      onCancel={working ? undefined : onCancel}
      closeIcon={<span className={styles.iactGlyph}>✕</span>}
      title={
        <div style={{ minWidth: 0 }}>
          <div className={styles.seModalT}>{title}</div>
          {subtitle && <div className={styles.seModalS}>{subtitle}</div>}
        </div>
      }
      footer={
        <div className={styles.seModalF}>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className={`${styles.btn} ${styles.btnG}`}
            disabled={working}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnP}`}
            disabled={working || overpaying}
            onClick={confirm}
          >
            <CgIcon name="check" size={16} stroke={2.4} />
            {working ? "Working…" : submitLabel}
          </button>
        </div>
      }
    >
      <div className={styles.seDue}>
        <span>Amount due</span>
        <b className={styles.tnum}>{formatRupees(payable)}</b>
      </div>

      <div style={{ marginTop: 14 }}>
        <div className={styles.seFlab}>
          <span>Payment mode</span>
        </div>
        <div className={styles.pmGrid}>
          {PAYMENT_MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              aria-pressed={mode === m.value}
              className={`${styles.pm} ${mode === m.value ? styles.on : ""}`}
              onClick={() => {
                setMode(m.value);
                setError("");
              }}
            >
              <span className={styles.pmIco}>
                <img src={m.icon} alt="" width={16} height={16} />
              </span>
              <span>{m.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.seFields} style={{ marginTop: 14 }}>
        <div>
          <div className={styles.seFlab}>
            <span>Amount received</span>
            <button
              type="button"
              onClick={() => {
                setAmount(String(payable));
                setError("");
              }}
            >
              Full amount
            </button>
          </div>

          <input
            className={styles.fi}
            inputMode="decimal"
            aria-label="Amount received"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setError("");
            }}
          />
        </div>
        {needsReference && (
          <div>
            <div className={styles.seFlab}>
              <span>Transaction number</span>
            </div>
            <input
              className={styles.fi}
              placeholder="Enter transaction ID"
              aria-label="Transaction number"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </div>
        )}
      </div>

      {error && <span className={styles.seErrText}>{error}</span>}

      <div className={styles.seBal} style={{ marginTop: 14 }}>
        <span>
          {!received
            ? "Nothing collected — the bill is raised unpaid"
            : balance > 0.009
              ? "Balance after this payment"
              : "Settles the bill in full"}
        </span>
        <b className={`${styles.tnum} ${balance > 0.009 ? styles.due : ""}`}>
          {formatRupees(Math.max(balance, 0))}
        </b>
      </div>
    </Modal>
  );
};

export default RecordPaymentModal;
